<template>
    <div class="editor-overlay" @click.self="$emit('close')">
        <div class="editor-modal">
            <!-- Modal header -->
            <div class="editor-header">
                <h2>{{ isEdit ? 'עריכת מודעה' : 'מודעה חדשה' }}</h2>
                <button class="editor-close-btn" @click="$emit('close')">✕</button>
            </div>

            <div class="editor-body">
                <!-- LEFT: Form -->
                <div class="editor-form-col">
                    <div class="form-scroll">

                        <!-- Section A: Basic Info -->
                        <div class="form-section">
                            <div class="section-title">פרטי המשרה</div>

                            <div class="form-field">
                                <label>כותרת המשרה <span class="req-star">*</span></label>
                                <input v-model.trim="form.title" type="text" placeholder="לדוגמה: קופאי/ת" />
                                <span v-if="errors.title" class="field-error">{{ errors.title }}</span>
                            </div>

                            <div class="form-field">
                                <label>סניף / מיקום <span class="req-star">*</span></label>
                                <input v-model.trim="form.branch" type="text" placeholder="לדוגמה: חיפה" />
                                <span v-if="errors.branch" class="field-error">{{ errors.branch }}</span>
                            </div>

                            <div class="form-field">
                                <label>היקף משרה</label>
                                <select v-model="form.employmentType">
                                    <option value="משרה מלאה">משרה מלאה</option>
                                    <option value="משרה חלקית">משרה חלקית</option>
                                    <option value="סטודנט/ית">סטודנט/ית</option>
                                </select>
                            </div>

                            <div class="form-field">
                                <label>שפת המודעה (קובעת כיוון טקסט)</label>
                                <select v-model="form.language">
                                    <option value="he">עברית (ימין לשמאל)</option>
                                    <option value="ru">רוסית (שמאל לימין)</option>
                                </select>
                            </div>

                            <div class="form-field">
                                <label>תיאור התפקיד</label>
                                <textarea v-model="form.description" rows="4" placeholder="תאר את התפקיד והאחריות..."></textarea>
                            </div>
                        </div>

                        <!-- Section B: Requirements -->
                        <div class="form-section">
                            <div class="section-title">דרישות</div>
                            <div v-for="(req, i) in form.requirements" :key="i" class="req-row">
                                <input v-model.trim="form.requirements[i]" type="text" :placeholder="`דרישה ${i + 1}`" />
                                <button class="req-remove-btn" @click="removeRequirement(i)" title="הסר">✕</button>
                            </div>
                            <button class="req-add-btn" @click="addRequirement">+ הוסף דרישה</button>
                        </div>

                        <!-- Section C: Image -->
                        <div class="form-section">
                            <div class="section-title">תמונה מייצגת</div>
                            <div v-if="form.image.url" class="image-preview-wrap">
                                <img :src="form.image.url" alt="תצוגה מקדימה" class="image-preview" />
                                <button class="image-remove-btn" @click="removeImage">החלף תמונה</button>
                            </div>
                            <div v-else>
                                <input type="file" accept="image/png,image/jpeg,image/jpg" @change="handleImageChange" class="file-input" />
                                <div v-if="imageUploading" class="image-uploading">
                                    <span class="spinner"></span>
                                    <span>מעלה תמונה ומייצר תצוגה מוקטנת...</span>
                                </div>
                                <span v-if="errors.image" class="field-error">{{ errors.image }}</span>
                            </div>
                        </div>

                        <!-- Section D: Apply Form Config -->
                        <div class="form-section">
                            <div class="section-title">הגדרות טופס מועמדות</div>
                            <p class="section-hint">בחר אילו שדות יוצגו למועמדים ואילו הם חובה</p>
                            <div class="config-table">
                                <div class="config-row config-header">
                                    <span>שדה</span>
                                    <span>הצג שדה</span>
                                    <span>חובה</span>
                                </div>
                                <div v-for="field in configFields" :key="field.key" class="config-row">
                                    <span class="config-field-name">{{ field.label }}</span>
                                    <label class="toggle-wrap">
                                        <input
                                            type="checkbox"
                                            v-model="form.applyFormConfig[field.key].enabled"
                                            @change="onEnabledChange(field.key)"
                                        />
                                        <span class="toggle"></span>
                                    </label>
                                    <label class="toggle-wrap">
                                        <input
                                            type="checkbox"
                                            v-model="form.applyFormConfig[field.key].required"
                                            :disabled="!form.applyFormConfig[field.key].enabled"
                                        />
                                        <span class="toggle" :class="{ disabled: !form.applyFormConfig[field.key].enabled }"></span>
                                    </label>
                                </div>
                            </div>

                            <!-- Optional multi-select questions: branches / roles -->
                            <div v-for="sel in selectionSections" :key="sel.kind" class="sel-card">
                                <div class="sel-card-head">
                                    <div class="sel-card-title">
                                        <i class="material-icons">{{ sel.icon }}</i>
                                        <div>
                                            <div class="sel-card-name">{{ sel.name }}</div>
                                            <div class="sel-card-question">"{{ sel.question.title }}"</div>
                                        </div>
                                    </div>
                                    <label class="toggle-wrap sel-toggle">
                                        <span class="sel-toggle-text">הצג למועמד</span>
                                        <input
                                            type="checkbox"
                                            v-model="form.applyFormConfig[sel.kind].enabled"
                                            @change="onEnabledChange(sel.kind)"
                                        />
                                        <span class="toggle"></span>
                                    </label>
                                </div>

                                <div v-if="form.applyFormConfig[sel.kind].enabled" class="sel-card-body">
                                    <label class="toggle-wrap sel-toggle">
                                        <input type="checkbox" v-model="form.applyFormConfig[sel.kind].required" />
                                        <span class="toggle"></span>
                                        <span class="sel-toggle-text">חובה לבחור לפחות אפשרות אחת</span>
                                    </label>

                                    <div class="sel-subtitle">{{ sel.pickerTitle }}</div>
                                    <OptionChipPicker v-model="form.applyFormConfig[sel.kind].options" :groups="sel.groups" />
                                    <span v-if="errors[sel.kind + 'Options']" class="field-error">{{ errors[sel.kind + 'Options'] }}</span>

                                    <button type="button" class="sel-rules-toggle" @click="toggleRules(sel.kind)">
                                        <i class="material-icons">{{ rulesOpen[sel.kind] ? 'expand_less' : 'expand_more' }}</i>
                                        <span>הגבלות שילוב (אופציונלי)</span>
                                        <span v-if="form.applyFormConfig[sel.kind].blockedPairs.length" class="sel-rules-badge">
                                            {{ form.applyFormConfig[sel.kind].blockedPairs.length }}
                                        </span>
                                    </button>
                                    <div v-if="rulesOpen[sel.kind]" class="sel-rules">
                                        <p class="section-hint">{{ sel.rulesHint }}</p>
                                        <CompatibilityGrid
                                            v-model="form.applyFormConfig[sel.kind].blockedPairs"
                                            :items="ruleItemsFor(sel.kind)"
                                            :empty-text="sel.rulesEmpty"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Section E: Email Notifications -->
                        <div class="form-section">
                            <div class="section-title">עדכונים במייל</div>
                            <p class="section-hint">בחר לאן ישלחו עדכונים כשמועמד/ת שולח/ת טופס למשרה זו</p>
                            <div class="email-mode-radios">
                                <label class="radio-label" :class="{ active: form.emailMode === 'global' }">
                                    <input type="radio" v-model="form.emailMode" value="global" />
                                    <span class="radio-dot"></span>
                                    <span>שימוש ברשימת המיילים הגלובלית</span>
                                </label>
                                <label class="radio-label" :class="{ active: form.emailMode === 'specific' }">
                                    <input type="radio" v-model="form.emailMode" value="specific" />
                                    <span class="radio-dot"></span>
                                    <span>כתובות מייל ספציפיות למשרה זו</span>
                                </label>
                            </div>
                            <div v-if="form.emailMode === 'specific'" class="specific-emails">
                                <p class="section-hint" v-if="globalEmailPool.length === 0">
                                    אין כתובות מייל ברשימה הגלובלית. יש להוסיף כתובות קודם בהגדרות הכלליות.
                                </p>
                                <div v-for="entry in globalEmailPool" :key="entry.email" class="email-check-row">
                                    <label class="email-check-label">
                                        <input
                                            type="checkbox"
                                            :value="entry.email"
                                            v-model="form.notificationEmails"
                                        />
                                        <span class="email-checkbox"></span>
                                        <span class="email-check-text" dir="ltr">{{ entry.email }}</span>
                                    </label>
                                </div>
                                <span v-if="errors.notificationEmails" class="field-error">{{ errors.notificationEmails }}</span>
                            </div>
                        </div>

                        <!-- Save button -->
                        <div class="form-actions">
                            <button class="save-btn" @click="handleSave" :disabled="isPending">
                                <span v-if="isPending" class="spinner"></span>
                                <span v-else>{{ isEdit ? 'שמור שינויים' : 'פרסם מודעה' }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- RIGHT: Live Preview -->
                <div class="editor-preview-col">
                    <div class="preview-label">תצוגה מקדימה</div>
                    <div class="preview-wrap">
                        <AdCard :ad="form" :preview="true" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { projectFirestore } from '@/firebase/config'
import use_job_ads_storage from '@/Methods/use_job_ads_storage'
import { slide_pop_successs, slide_pop_error } from '@/Methods/Msgs'
import AdCard from '@/components/Drushim/AdCard.vue'
import OptionChipPicker from './OptionChipPicker.vue'
import CompatibilityGrid from './CompatibilityGrid.vue'
import {
    normalizeApplyFormConfig, sanitizeSelectionConfig, groupedOptions, ruleItems, QUESTIONS, SELECTION_KINDS
} from '@/Methods/job_ads_options'

// The optional multi-select questions (branches / roles) shown under the field table
const SELECTION_SECTIONS = [
    {
        kind: 'branches',
        icon: 'store',
        name: 'בחירת סניפים',
        question: QUESTIONS.branches,
        pickerTitle: 'אילו סניפים להציע למועמד?',
        groups: groupedOptions('branches'),
        rulesHint: 'סמנו ✗ בין אזורים שלא ניתן לשלב באותה מועמדות. כשהמועמד יבחר סניף מאזור מסוים, הסניפים באזורים המסומנים ✗ ייחסמו עבורו.',
        rulesEmpty: 'יש להציע סניפים משני אזורים לפחות כדי להגדיר הגבלות שילוב.'
    },
    {
        kind: 'roles',
        icon: 'badge',
        name: 'בחירת תפקידים',
        question: QUESTIONS.roles,
        pickerTitle: 'אילו תפקידים להציע למועמד?',
        groups: groupedOptions('roles'),
        rulesHint: 'סמנו ✗ בין תפקידים שלא ניתן לשלב באותה מועמדות.',
        rulesEmpty: 'יש להציע לפחות שני תפקידים כדי להגדיר הגבלות שילוב.'
    }
]

export default {
    name: 'AdEditor',
    components: { AdCard, OptionChipPicker, CompatibilityGrid },
    props: {
        ad: { type: Object, default: null }
    },
    emits: ['close', 'saved'],
    setup(props, { emit }) {
        const { uploadAdImage, imageUrl, imageFilePath, deleteFile } = use_job_ads_storage()
        const isPending = ref(false)
        const imageUploading = ref(false)
        const errors = ref({})

        const isEdit = computed(() => !!props.ad)

        const configFields = [
            { key: 'fullName', label: 'שם מלא' },
            { key: 'phone',    label: 'טלפון' },
            { key: 'email',    label: 'דוא"ל' },
            { key: 'idNumber', label: 'ת.ז.' },
            { key: 'message',  label: 'הודעה חופשית' },
            { key: 'cv',       label: 'קורות חיים (קובץ)' }
        ]

        const buildForm = () => {
            if (props.ad) {
                return {
                    title: props.ad.title || '',
                    branch: props.ad.branch || '',
                    description: props.ad.description || '',
                    requirements: props.ad.requirements ? [...props.ad.requirements] : [''],
                    employmentType: props.ad.employmentType || 'משרה מלאה',
                    language: props.ad.language || 'he',
                    image: props.ad.image ? { ...props.ad.image } : { url: '', file_path: '' },
                    // Fresh copy with defaults for keys added after the ad was saved
                    applyFormConfig: normalizeApplyFormConfig(props.ad.applyFormConfig),
                    emailMode: props.ad.emailMode || 'global',
                    notificationEmails: props.ad.notificationEmails ? [...props.ad.notificationEmails] : []
                }
            }
            return {
                title: '',
                branch: '',
                description: '',
                requirements: [''],
                employmentType: 'משרה מלאה',
                language: 'he',
                image: { url: '', file_path: '' },
                applyFormConfig: normalizeApplyFormConfig(null),
                emailMode: 'global',
                notificationEmails: []
            }
        }

        const form = ref(buildForm())

        // Selection questions: a rule grid starts open only when rules already exist
        const selectionSections = SELECTION_SECTIONS
        const rulesOpen = ref(Object.fromEntries(
            SELECTION_KINDS.map(kind => [kind, form.value.applyFormConfig[kind].blockedPairs.length > 0])
        ))
        const toggleRules = (kind) => { rulesOpen.value[kind] = !rulesOpen.value[kind] }
        // Grid rows/cols follow what is currently offered (regions with ≥1 branch / offered roles)
        const ruleItemsFor = (kind) => ruleItems(kind, form.value.applyFormConfig[kind].options)

        const addRequirement = () => {
            form.value.requirements.push('')
        }
        const removeRequirement = (i) => {
            if (form.value.requirements.length > 1) {
                form.value.requirements.splice(i, 1)
            }
        }

        const handleImageChange = async (e) => {
            const file = e.target.files[0]
            if (!file) return
            const types = ['image/png', 'image/jpeg', 'image/jpg']
            if (!types.includes(file.type)) {
                slide_pop_error('סוג קובץ זה אינו נתמך. יש להעלות PNG או JPG')
                return
            }
            imageUploading.value = true
            errors.value.image = ''
            try {
                await uploadAdImage(file, form.value.title || 'job_ad')
                form.value.image.url = imageUrl.value
                form.value.image.file_path = imageFilePath.value
            } catch (err) {
                console.error('[AdEditor] image upload failed:', err)
                errors.value.image = `שגיאה בהעלאת התמונה: ${err?.message || err}`
            } finally {
                imageUploading.value = false
            }
        }

        const removeImage = async () => {
            if (form.value.image.file_path) {
                await deleteFile(form.value.image.file_path)
            }
            form.value.image = { url: '', file_path: '' }
        }

        const onEnabledChange = (key) => {
            if (!form.value.applyFormConfig[key].enabled) {
                form.value.applyFormConfig[key].required = false
            }
        }

        // Global email pool for per-job selection
        const globalEmailPool = ref([])

        const fetchGlobalEmailPool = async () => {
            try {
                const doc = await projectFirestore.collection('Utils').doc('JobAdsConfig').get()
                if (doc.exists && doc.data().notificationEmails) {
                    const raw = doc.data().notificationEmails
                    globalEmailPool.value = raw.map(entry =>
                        typeof entry === 'string'
                            ? { email: entry }
                            : { email: entry.email || '' }
                    ).filter(e => e.email)
                }
            } catch { /* silent */ }
        }

        onMounted(() => {
            fetchGlobalEmailPool()
        })

        const validate = () => {
            const e = {}
            if (!form.value.title) e.title = 'שדה חובה'
            if (!form.value.branch) e.branch = 'שדה חובה'
            if (form.value.emailMode === 'specific' && form.value.notificationEmails.length === 0) {
                e.notificationEmails = 'יש לבחור לפחות כתובת מייל אחת'
            }
            SELECTION_KINDS.forEach(kind => {
                const cfg = form.value.applyFormConfig[kind]
                if (cfg.enabled && cfg.options.length === 0) {
                    e[kind + 'Options'] = 'יש לבחור לפחות אפשרות אחת להצגה למועמד'
                }
            })
            errors.value = e
            return Object.keys(e).length === 0
        }

        // Plain, sanitized copy for Firestore: unknown option ids and orphan rule pairs dropped
        const buildApplyFormConfig = () => {
            const cfg = normalizeApplyFormConfig(form.value.applyFormConfig)
            SELECTION_KINDS.forEach(kind => { cfg[kind] = sanitizeSelectionConfig(cfg[kind], kind) })
            return cfg
        }

        const handleSave = async () => {
            if (!validate()) return
            isPending.value = true
            try {
                const data = {
                    title: form.value.title,
                    branch: form.value.branch,
                    description: form.value.description,
                    requirements: form.value.requirements.filter(r => r.trim()),
                    employmentType: form.value.employmentType,
                    language: form.value.language,
                    image: form.value.image,
                    applyFormConfig: buildApplyFormConfig(),
                    emailMode: form.value.emailMode,
                    notificationEmails: form.value.emailMode === 'specific'
                        ? [...form.value.notificationEmails]
                        : []
                }

                if (isEdit.value) {
                    await projectFirestore.collection('JobAds').doc(props.ad.id).update({
                        ...data,
                        updatedAt: new Date()
                    })
                } else {
                    await projectFirestore.collection('JobAds').add({
                        ...data,
                        active: true,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    })
                }

                slide_pop_successs(isEdit.value ? 'המודעה עודכנה בהצלחה' : 'המודעה פורסמה בהצלחה')
                emit('saved')
                emit('close')
            } catch (err) {
                slide_pop_error('אירעה שגיאה בשמירה')
            } finally {
                isPending.value = false
            }
        }

        return {
            form, isEdit, errors, isPending, imageUploading, configFields, globalEmailPool,
            selectionSections, rulesOpen, toggleRules, ruleItemsFor,
            addRequirement, removeRequirement, handleImageChange, removeImage,
            onEnabledChange, handleSave
        }
    }
}
</script>

<style scoped>
.editor-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 8000;
    padding: 20px;
}
.editor-modal {
    background: #ffffff;
    border-radius: 16px;
    width: 100%;
    max-width: 1100px;
    height: 90vh;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Header */
.editor-header {
    background: linear-gradient(135deg, #1579F2 0%, #0d5bbf 100%);
    padding: 18px 24px;
    border-radius: 16px 16px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}
.editor-header h2 {
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    margin: 0;
}
.editor-close-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: #fff;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
}
.editor-close-btn:hover {
    background: rgba(255, 255, 255, 0.35);
}

/* Body */
.editor-body {
    display: grid;
    grid-template-columns: 1fr 380px;
    min-height: 600px;
}
@media (max-width: 900px) {
    .editor-body {
        grid-template-columns: 1fr;
    }
}

/* Form column */
.editor-form-col {
    border-left: 1px solid #eee;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.form-scroll {
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1;
}

/* Form sections */
.form-section {
    background: #fafafa;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
}
.section-title {
    font-size: 15px;
    font-weight: 700;
    color: #1579F2;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e8f0fe;
}
.section-hint {
    font-size: 12px;
    color: #888;
    margin: -8px 0 12px;
}
.form-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 12px;
}
.form-field:last-child {
    margin-bottom: 0;
}
.form-field label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
}
.req-star {
    color: #bb2429;
    margin-right: 2px;
}
.form-field input[type="text"],
.form-field select,
.form-field textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 14px;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    font-size: 14px;
    color: #333;
    background: #fff;
    outline: none;
    font-family: inherit;
    transition: border-color 0.2s, box-shadow 0.2s;
}
.form-field select {
    padding-left: 8px;
}
.form-field textarea {
    border-radius: 12px;
    resize: vertical;
}
.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
    border-color: #1579F2;
    box-shadow: 0 0 0 3px rgba(21, 121, 242, 0.1);
}
.field-error {
    font-size: 12px;
    color: #bb2429;
    padding-right: 4px;
}

/* Requirements */
.req-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}
.req-row input {
    flex: 1;
    padding: 8px 14px;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    font-size: 13px;
    outline: none;
    font-family: inherit;
    transition: border-color 0.2s;
}
.req-row input:focus {
    border-color: #1579F2;
}
.req-remove-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid #ccc;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    color: #bb2429;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
}
.req-remove-btn:hover {
    background: #fff0f0;
}
.req-add-btn {
    background: none;
    border: 1.5px dashed #1579F2;
    color: #1579F2;
    border-radius: 20px;
    padding: 7px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    font-family: inherit;
}
.req-add-btn:hover {
    background: #EBF4FF;
}

/* Image */
.file-input {
    font-size: 13px;
}
.image-preview-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
}
.image-preview {
    width: 100%;
    max-height: 160px;
    object-fit: cover;
    border-radius: 10px;
    border: 1px solid #eee;
}
.image-remove-btn {
    background: none;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    padding: 6px 16px;
    font-size: 13px;
    color: #555;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.2s;
}
.image-remove-btn:hover {
    border-color: #1579F2;
    color: #1579F2;
}
.image-uploading {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #555;
    margin-top: 8px;
}

/* Apply Form Config */
.config-table {
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    overflow: hidden;
}
.config-row {
    display: grid;
    grid-template-columns: 1fr 80px 80px;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid #f0f0f0;
}
.config-row:last-child {
    border-bottom: none;
}
.config-header {
    background: #f5f5f5;
    font-size: 12px;
    font-weight: 700;
    color: #555;
    text-align: center;
}
.config-header span:first-child {
    text-align: right;
}
.config-field-name {
    font-size: 13px;
    color: #333;
    font-weight: 600;
}
.toggle-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}
.toggle-wrap input[type="checkbox"] {
    display: none;
}
.toggle {
    width: 36px;
    height: 20px;
    background: #ccc;
    border-radius: 20px;
    position: relative;
    transition: background 0.25s;
    cursor: pointer;
}
.toggle::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    top: 2px;
    right: 2px;
    transition: right 0.25s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.toggle-wrap input:checked + .toggle {
    background: #1579F2;
}
.toggle-wrap input:checked + .toggle::after {
    right: 18px;
}
.toggle.disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

/* Optional selection questions (branches / roles) */
.sel-card {
    margin-top: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
}
.sel-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    background: #f5f5f5;
}
.sel-card-title {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.sel-card-title i {
    color: #1579F2;
    font-size: 22px;
}
.sel-card-name {
    font-size: 14px;
    font-weight: 700;
    color: #333;
}
.sel-card-question {
    font-size: 12px;
    color: #777;
}
.sel-toggle {
    justify-content: flex-start;
    gap: 8px;
    flex-shrink: 0;
}
.sel-toggle-text {
    font-size: 13px;
    color: #333;
    font-weight: 600;
    white-space: nowrap;
}
.sel-card-body {
    padding: 12px 14px 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.sel-subtitle {
    font-size: 13px;
    font-weight: 700;
    color: #333;
    margin-bottom: -4px;
}
.sel-rules-toggle {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    padding: 6px 14px 6px 10px;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.2s, color 0.2s;
}
.sel-rules-toggle:hover {
    border-color: #1579F2;
    color: #1579F2;
}
.sel-rules-toggle i {
    font-size: 18px;
}
.sel-rules-badge {
    background: #1579F2;
    color: #fff;
    border-radius: 10px;
    font-size: 11px;
    padding: 1px 7px;
    font-weight: 700;
}
.sel-rules {
    border: 1px dashed #cfd8e3;
    border-radius: 10px;
    padding: 12px;
    background: #fafcff;
}
@media (max-width: 600px) {
    .sel-card-head {
        flex-wrap: wrap;
    }
}

/* Email notifications */
.email-mode-radios {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
}
.radio-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border: 1.5px solid #ddd;
    border-radius: 10px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    transition: border-color 0.2s, background 0.2s;
}
.radio-label:hover {
    border-color: #1579F2;
}
.radio-label.active {
    border-color: #1579F2;
    background: #EBF4FF;
}
.radio-label input[type="radio"] {
    display: none;
}
.radio-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #ccc;
    flex-shrink: 0;
    position: relative;
    transition: border-color 0.2s;
}
.radio-label.active .radio-dot {
    border-color: #1579F2;
}
.radio-label.active .radio-dot::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    background: #1579F2;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.specific-emails {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.email-check-row {
    display: flex;
    align-items: center;
}
.email-check-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1.5px solid #eee;
    border-radius: 8px;
    cursor: pointer;
    width: 100%;
    transition: background 0.15s, border-color 0.15s;
}
.email-check-label:hover {
    background: #f8f9fa;
    border-color: #1579F2;
}
.email-check-label input[type="checkbox"] {
    display: none;
}
.email-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid #ccc;
    border-radius: 4px;
    position: relative;
    flex-shrink: 0;
    transition: border-color 0.2s, background 0.2s;
}
.email-check-label input:checked + .email-checkbox {
    background: #1579F2;
    border-color: #1579F2;
}
.email-check-label input:checked + .email-checkbox::after {
    content: '';
    position: absolute;
    width: 5px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    top: 2px;
    left: 6px;
    transform: rotate(45deg);
}
.email-check-text {
    font-size: 14px;
    color: #333;
    font-weight: 500;
}

/* Save button */
.form-actions {
    display: flex;
    justify-content: center;
    padding-top: 4px;
}
.save-btn {
    height: 50px;
    min-width: 180px;
    background: #FEF200;
    color: #000;
    border: 1.5px solid #000;
    border-radius: 50px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s, transform 0.1s;
    font-family: inherit;
}
.save-btn:hover:not(:disabled) {
    opacity: 0.85;
    transform: scale(1.02);
}
.save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Preview column */
.editor-preview-col {
    background: #F5F4EF;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow-y: auto;
    min-height: 200px;
}
.preview-label {
    font-size: 13px;
    font-weight: 700;
    color: #888;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.preview-wrap {
    flex: 1;
}

/* Spinner */
.spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid #000;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
